/* RegexControlComponent.js */

"use strict";
const _ = require("lodash");
const InputBaseTextfieldControlComponent = require("@gluenetworks/input_base_control").Textfield;
const Renderer = require("./RegexControlRenderer");
const RegexUtils = require("./RegexUtils");
const RegexMode = require("./RegexMode");
const DEFAULT_MIN_LINE_COUNT = 5;
const DEFAULT_MAX_LINE_COUNT = 20;
const DEFAULT_LABEL = "Test String/CLI";

class RegexControlComponent extends InputBaseTextfieldControlComponent {
    /**
     * @schema.name Regex control
     * @schema.package @gluenetworks/regex_control
     * @schema.packageIdentifier RegexControlComponent
     * @schema.namespace require("gluenetworks/regex_control").RegexControlComponent
     * @schema.events ["Disabled", "Invalid", "Value", "Visible", "DarkTheme", "MinLineCount", "Regex", "Mode", "SectionDelimiter", "ResizeEditor", "InProgress", "ModeSelectorVisible", "Label"]
     *
     * @schema.configuration.minLineCount.name minLineCount
     * @schema.configuration.minLineCount.type integer
     * @schema.configuration.minLineCount.default 5
     * @schema.configuration.minLineCount.required false
     *
     * @schema.configuration.maxLineCount.name maxLineCount
     * @schema.configuration.maxLineCount.type integer
     * @schema.configuration.maxLineCount.default 20
     * @schema.configuration.maxLineCount.required false
     *
     * @schema.configuration.regex.name regex
     * @schema.configuration.regex.type string
     * @schema.configuration.regex.default (?<deviceId>^[\w\d\-\.()]+)\s+(?<localPort>(?:[\w]+\s?[\d\/\-\.]+))\s+(?<holdtime>\d+)\s+(?<capability>[RSDTHCBIMPr\s]+\b)\s+(?<platform>(?:\b[\S+]+\s?[\S+]+))\s+(?<remotePort>(?:[\w]+\s?[\d\/\-\.]+)$)
     * @schema.configuration.regex.required false
     *
     * @schema.configuration.mode.name mode
     * @schema.configuration.mode.type string
     * @schema.configuration.mode.default match_all_lines
     * @schema.configuration.mode.required false
     *
     * @schema.configuration.sectionDelimiter.name sectionDelimiter
     * @schema.configuration.sectionDelimiter.type string
     * @schema.configuration.sectionDelimiter.default undefined
     * @schema.configuration.sectionDelimiter.required false
     *
     * @schema.configuration.modeSelectorVisible.name modeSelectorVisible
     * @schema.configuration.modeSelectorVisible.type boolean
     * @schema.configuration.modeSelectorVisible.default true
     * @schema.configuration.modeSelectorVisible.required false
     *
     * @schema.configuration.label.name label
     * @schema.configuration.label.type string
     * @schema.configuration.label.default 'Test String/CLI'
     * @schema.configuration.label.required false
     *
     * @schema.configuration.autoResize.name autoResize
     * @schema.configuration.autoResize.type boolean
     * @schema.configuration.autoResize.default false
     * @schema.configuration.autoResize.required false
     *
     */
    constructor(configOptions) {
        super(configOptions);
        configOptions = configOptions || {};
        this._minLineCount = configOptions.minLineCount || DEFAULT_MIN_LINE_COUNT;
        this._maxLineCount = configOptions.maxLineCount || DEFAULT_MAX_LINE_COUNT;
        this._regex =
            configOptions.regex && RegexUtils.isRegexValid(configOptions.regex)
                ? configOptions.regex
                : "";
        this._mode = RegexMode.isValidMode(configOptions.mode)
            ? configOptions.mode
            : RegexMode.MATCH_SINGLE_LINES.value;
        this._sectionDelimiter = configOptions.sectionDelimiter || "";
        this._modeSelectorVisible = configOptions.modeSelectorVisible === false ? false : true;
        this._inProgress = false;
        this._label = _.isNil(configOptions.label) ? DEFAULT_LABEL : configOptions.label;
        this._autoResize = configOptions.autoResize === true;
        this._renderer = new Renderer(this);
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}

    _unregisterListeners() {}
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._renderer = undefined;
            },
            () => {
                //Error handling on renderer shut down
            }
        );
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    _triggerInProgress(newInProgressStatus) {
        if (this._inProgress !== newInProgressStatus) {
            let oldInProgressStatus = this._inProgress;
            this._inProgress = newInProgressStatus;
            this.emit("InProgress", newInProgressStatus, oldInProgressStatus);
        }
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    get minLineCount() {
        return this.getMinLineCount();
    }

    /**
     * @schema.methods.getMinLineCount.name getMinLineCount
     * @schema.methods.getMinLineCount.description Returns min lines displayed on the editor
     * @schema.methods.getMinLineCount.returns {integer}
     */
    getMinLineCount() {
        return this._minLineCount;
    }

    get maxLineCount() {
        return this.getMaxLineCount();
    }

    /**
     * @schema.methods.getMaxLineCount.name getMaxLineCount
     * @schema.methods.getMaxLineCount.description Returns max lines displayed on the editor
     * @schema.methods.getMaxLineCount.returns {integer}
     */
    getMaxLineCount() {
        return this._maxLineCount;
    }

    /**
     * @schema.methods.setRegex.name setRegex
     * @schema.methods.setRegex.description Sets the regular expression to highlight the matching editor text
     * @schema.methods.setRegex.returns Instance of the control
     * @schema.methods.setRegex.parameters.newVal.name regex
     * @schema.methods.setRegex.parameters.newVal.description Regular expression
     * @schema.methods.setRegex.parameters.newVal.type {string}
     */
    setRegex(newVal) {
        if (this._regex !== newVal) {
            this._regex = newVal && RegexUtils.isRegexValid(newVal) ? newVal : "";
            this.emit("Regex", this._regex);
        }
        return this;
    }

    get regex() {
        return this._regex;
    }

    /**
     * @schema.methods.getRegex.name getRegex
     * @schema.methods.getRegex.description Returns regular expression value
     * @schema.methods.getRegex.returns {string}
     */
    getRegex() {
        return this._regex;
    }

    /**
     * @schema.methods.setMode.name setMode
     * @schema.methods.setMode.description If 'singleline', looks for matches of given regex in each line
     *      else if 'global'
     *          looks for matches of given regex across lines (similar to multiline flag of regex)
     *      else if 'section'
     *          identifies section sections of text in editor. And looks for matches of regex only on first line of each section. If a match is found
     *          highlights the entire section.
     *          sections are identified based on given section delimiter. if no section delimiter is given, a line starting with no indentation is considered start of a section
     *          and all following indented lines are considered part of this section.
     * @schema.methods.setMode.returns Instance of the control
     * @schema.methods.setMode.parameters.newVal.name newVal
     * @schema.methods.setMode.parameters.newVal.description decides if a simple search of given regex should be performed or
     *      a complex section identification and regex matching logic
     * @schema.methods.setMode.parameters.newVal.type string
     */
    setMode(newVal) {
        if (newVal === this._mode || !RegexMode.isValidMode(newVal)) {
            return this;
        }
        this._mode = newVal;
        this.emit("Mode", this._mode);
        return this;
    }

    /**
     * @schema.methods.getMode.name getMode
     * @schema.methods.getMode.description returns boolean indicating if this control is working in normal search mode
     * @schema.methods.getMode.returns {string}
     */
    getMode() {
        return this._mode;
    }

    get mode() {
        return this.getMode();
    }

    set mode(newVal) {
        return this.setMode(newVal);
    }

    /**
     * @schema.methods.setSectionDelimiter.name setSectionDelimiter
     * @schema.methods.setSectionDelimiter.description Sets the section delimiter string
     * @schema.methods.setSectionDelimiter.returns Instance of the control
     * @schema.methods.setSectionDelimiter.parameters.newVal.name newVal
     * @schema.methods.setSectionDelimiter.parameters.newVal.description delimiter string used to split editor text into meaningful sections/blocks
     * @schema.methods.setSectionDelimiter.parameters.newVal.type {string}
     */
    setSectionDelimiter(newVal) {
        if (this._sectionDelimiter !== newVal) {
            this._sectionDelimiter = newVal ? newVal : "";
            this.emit("SectionDelimiter", this._sectionDelimiter);
        }
        return this;
    }

    set sectionDelimiter(newVal) {
        return this.setSectionDelimiter(newVal);
    }

    get sectionDelimiter() {
        return this._sectionDelimiter;
    }

    /**
     * @schema.methods.getSectionDelimiter.name getSectionDelimiter
     * @schema.methods.getSectionDelimiter.description Returns section delimiter value
     * @schema.methods.getSectionDelimiter.returns {string}
     */
    getSectionDelimiter() {
        return this._sectionDelimiter;
    }

    /**
     * @schema.methods.setModeSelectorVisible.name setModeSelectorVisible
     * @schema.methods.setModeSelectorVisible.description Show/hide mode selector drop down
     * @schema.methods.setModeSelectorVisible.returns Instance of the control
     * @schema.methods.setModeSelectorVisible.parameters.visible.name visible
     * @schema.methods.setModeSelectorVisible.parameters.visible.description if true, show mode selector drop down else hide mode selector drop down
     * @schema.methods.setModeSelectorVisible.parameters.visible.type {boolean}
     */
    setModeSelectorVisible(visible) {
        if (this._modeSelectorVisible !== visible) {
            this._modeSelectorVisible = visible === true ? visible : false;
            this.emit("ModeSelectorVisible", this._modeSelectorVisible);
        }
        return this;
    }

    set modeSelectorVisible(visible) {
        return this.setModeSelectorVisible(visible);
    }

    /**
     * @schema.methods.isModeSelectorVisible.name isModeSelectorVisible
     * @schema.methods.isModeSelectorVisible.description Returns if mode selector is displayed
     * @returns {boolean|*}
     */
    isModeSelectorVisible() {
        return this._modeSelectorVisible;
    }

    get modeSelectorVisible() {
        return this.isModeSelectorVisible();
    }

    /**
     * @schema.methods.isInProgress.name isInProgress
     * @schema.methods.isInProgress.description Returns in progress status of this component
     * @schema.methods.isInProgress.returns {boolean}
     */
    isInProgress() {
        return this._inProgress;
    }

    get inProgress() {
        return this.isInProgress();
    }

    /**
     * @schema.methods.getLabel.name getLabel
     * @schema.methods.getLabel.description Returns label string displayed above the editor
     * @schema.methods.getLabel.returns {string}
     */
    getLabel() {
        return this._label;
    }

    /**
     * @schema.methods.setLabel.name setLabel
     * @schema.methods.setLabel.description Updates the label displayed above the editor
     * @schema.methods.setLabel.parameters.newLabel.name newLabel
     * @schema.methods.setLabel.parameters.newLabel.description New label value
     * @schema.methods.setLabel.parameters.newLabel.type {string}
     */
    setLabel(newLabel) {
        if (this._label === newLabel) {
            return this;
        }
        this._label = _.isNil(newLabel) ? DEFAULT_LABEL : newLabel;
        this.emit("Label", this._label);
        return this;
    }

    get label() {
        return this.getLabel();
    }

    set label(newLabel) {
        return this.setLabel(newLabel);
    }
    /**
     * @schema.methods.resizeEditor.name resizeEditor
     * @schema.methods.resizeEditor.description resize editor with given height
     * @schema.methods.resizeEditor.parameters.newHeight.name newHeight
     * @schema.methods.resizeEditor.parameters.newHeight.description new height of editor
     * @schema.methods.resizeEditor.parameters.newHeight.type {string}
     */
    resizeEditor(newHeight) {
        this.emit("ResizeEditor", newHeight);
    }

    /**
     * @schema.methods.setAutoResize.name setAutoResize
     * @schema.methods.setAutoResize.description if true, auto resizes the regex editor
     * @schema.methods.setAutoResize.parameters.newAutoResize.name newAutoResize
     * @schema.methods.setAutoResize.parameters.newAutoResize.description New auto resize flag
     * @schema.methods.setAutoResize.parameters.newAutoResize.type {boolean}
     */
    setAutoResize(newAutoResize) {
        if (this._autoResize !== newAutoResize && _.isBoolean(newAutoResize)) {
            this._autoResize = newAutoResize;
            this.emit("AutoResize", this._autoResize);
        }
        return this;
    }

    /**
     * @schema.methods.getAutoResize.name getAutoResize
     * @schema.methods.getAutoResize.description Returns a flag indicating if the regex editor auto resizes based on the height of its root element
     * @schema.methods.getAutoResize.returns {boolean}
     */
    getAutoResize() {
        return this._autoResize;
    }

    get autoResize() {
        return this.getAutoResize();
    }

    set autoResize(newAutoResize) {
        return this.setAutoResize(newAutoResize);
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = RegexControlComponent;
